import { Userpilot } from 'userpilot';
import { Serializer, Deserializer } from 'jsonapi-serializer';
import { RootState } from 'state/store';
import { selectUserEmail, selectOrganizationId } from 'state/User/userSlice';
import { BulkUpdateTasksResponse } from 'api/tasksList';
import { JsonApiResource, JsonApiResponse } from 'types/store/jsonApi';
import { TaskDetailToUpdate } from 'types/store/taskDetail';
import { TaskListFilters } from 'types/store/tasksList';
import { USERPILOT_EVENTS } from 'utils/constants';

export interface GetBulkUpdateTasksUrlParams {
  filters?: TaskListFilters;
  exportState?: {
    selectAll: boolean;
    excludedIds: string[];
    selectedIds: string[];
  };
  projectId: string;
}

export const getBulkUpdateTasksUrl: (
  params: GetBulkUpdateTasksUrlParams
) => string = (params) => {
  const baseUrl = `/v3/projects/${params.projectId}/tasks-assignees`;
  const urlParams = new URLSearchParams();
  const { filters, exportState } = params;
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => {
          urlParams.append(`filters[${key}][]`, v);
        });
      } else {
        if (value) {
          urlParams.append(`filters[${key}]`, value.toString());
        }
      }
    });
  }
  if (exportState) {
    if (exportState.selectAll) {
      urlParams.append('selectAll', exportState.selectAll.toString());
    }
    if (exportState.excludedIds && exportState.excludedIds.length > 0) {
      exportState.excludedIds.forEach((id) => {
        urlParams.append('excludedIds[]', id);
      });
    }
    if (exportState.selectedIds && exportState.selectedIds.length > 0) {
      exportState.selectedIds.forEach((id) => {
        urlParams.append('selectedIds[]', id);
      });
    }
  }
  return `${baseUrl}?${urlParams.toString()}`;
};

export const getBulkUpdateTasksRequestBody = (
  task: Partial<TaskDetailToUpdate>
): JsonApiResource<TaskDetailToUpdate> => {
  return new Serializer('task-bulk-update', {
    id: 'id',
    attributes: [
      'name',
      'description',
      'status',
      'type',
      'startDate',
      'dueDate',
      'estimateHours',
      'assignees',
      'disabled',
      'completionDate',
    ],
    relationships: [
      {
        relation: 'assignees',
        attributes: ['toAdd', 'toRemove'],
      },
    ],
    pluralizeType: false,
  }).serialize(task);
};

export const deserializeBulkUpdateTasksResponse = (
  response: JsonApiResponse<BulkUpdateTasksResponse>
) => {
  return new Deserializer({
    keyForAttribute: 'camelCase',
    transform: (record: BulkUpdateTasksResponse) => {
      return {
        attemptedCount: record.attemptedCount,
        successfulCount: record.successfulCount,
        failedCount: record.failedCount,
        operationId: record.operationId,
        successfulTaskIds: record.successfulTaskIds,
        failedTaskIds: record.failedTaskIds,
      };
    },
  }).deserialize(response);
};

export const trackBulkAssignEvent = (
  state: RootState,
  response: BulkUpdateTasksResponse
) => {
  const userEmail = selectUserEmail(state);
  const organizationId = selectOrganizationId(state);
  Userpilot.track(USERPILOT_EVENTS.TASK_BULK_ASSIGN, {
    email: userEmail,
    companyId: organizationId,
    operationId: response.operationId,
    successfulCount: response.successfulCount,
    failedCount: response.failedCount,
    totalCount: response.successfulCount + response.failedCount,
  });
};
